import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession, signOut } from '../lib/auth-client';
import { LayoutDashboard, Ticket, Users, LogOut, Sun, Moon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/theme';
import { BrandLogo } from './BrandLogo';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/tickets', icon: Ticket, label: 'Tickets', exact: false },
];

const ADMIN_NAV_ITEMS = [
  { to: '/users', icon: Users, label: 'Users', exact: true },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: Props) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === 'ADMIN';

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const items = isAdmin ? [...NAV_ITEMS, ...ADMIN_NAV_ITEMS] : NAV_ITEMS;

  function isActive(to: string, exact: boolean) {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(to + '/');
  }

  function handleNavClick() {
    onClose();
  }

  return (
    <aside
      style={{ backgroundColor: 'var(--sidebar)', borderColor: 'var(--sidebar-border)' }}
      className={cn(
        'flex flex-col w-[240px] shrink-0 h-screen border-r transition-transform duration-300',
        // Mobile: fixed off-canvas drawer
        'fixed inset-y-0 left-0 z-50',
        // Desktop: static in flow
        'lg:relative lg:z-auto lg:translate-x-0 lg:transition-none',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      {/* Brand */}
      <div className="px-5 h-14 flex items-center justify-between border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
        <BrandLogo />
        {/* Close button — mobile only */}
        <button
          onClick={onClose}
          className="lg:hidden flex items-center justify-center w-8 h-8 rounded-md transition-colors"
          style={{ color: 'var(--sidebar-text-dim)' }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        <p
          className="px-3 mb-2 text-[10px] font-bold tracking-[0.15em] uppercase"
          style={{ color: 'var(--sidebar-text-dim)' }}
        >
          Navigation
        </p>
        {items.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive(to, exact);
          return (
            <Link
              key={to}
              to={to}
              onClick={handleNavClick}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] font-medium transition-all duration-150'
              )}
              style={
                active
                  ? {
                      background: 'oklch(0.65 0.22 285 / 0.13)',
                      border: '1px solid oklch(0.65 0.22 285 / 0.22)',
                      color: 'var(--sidebar-primary)',
                    }
                  : {
                      border: '1px solid transparent',
                      color: 'var(--sidebar-text-muted)',
                    }
              }
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLAnchorElement).style.color = 'var(--sidebar-foreground)';
                  (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sidebar-hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLAnchorElement).style.color = 'var(--sidebar-text-muted)';
                  (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                }
              }}
            >
              <Icon className="h-[15px] w-[15px] shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      {session?.user && (
        <div className="px-3 py-4 border-t" style={{ borderColor: 'var(--sidebar-border)' }}>
          {/* User card */}
          <div className="px-3 py-2.5 rounded-md mb-2" style={{ background: 'var(--sidebar-user-bg)' }}>
            <p className="text-[12px] font-semibold truncate" style={{ color: 'var(--sidebar-foreground)' }}>
              {session.user.name}
            </p>
            <p className="text-[11px] truncate font-mono-data" style={{ color: 'var(--sidebar-text-dim)' }}>
              {session.user.email}
            </p>
          </div>

          {/* Theme toggle + Sign out row */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggle}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex items-center justify-center w-8 h-8 rounded-md transition-all duration-150 shrink-0"
              style={{ color: 'var(--sidebar-text-dim)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'oklch(0.65 0.22 285)';
                (e.currentTarget as HTMLButtonElement).style.background = 'oklch(0.65 0.22 285 / 0.1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--sidebar-text-dim)';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>

            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 flex-1 px-2.5 py-2 rounded-md text-[13px] transition-all duration-150"
              style={{ color: 'var(--sidebar-text-dim)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'oklch(0.65 0.22 25)';
                (e.currentTarget as HTMLButtonElement).style.background = 'oklch(0.65 0.22 25 / 0.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--sidebar-text-dim)';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <LogOut className="h-3.5 w-3.5 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
